import React, { useContext, useMemo, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'

// components
import { Button, ButtonType } from '@/components/atoms/button'

import { Cascader, Select } from 'antd'
import { PosCalculateModal } from '../../accommodation-management/pos-calculate-modal'
import { PluginCode, checkActivePlugin } from '@/libs/plugins'
import { AccountContext } from '@/contexts/account'
import { TransactionType } from '@/models/smaregi/transaction'
import { TerminalCalculateModal } from '../../accommodation-management/terminal-calculate-modal'
import { terminalStorageKey } from '@/constants/self-checkin-device-storage'
import { cascaderNameSearch } from '@/libs/cascader-name-search'
import { PaymentSettingType } from '@/models/sales'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { AdvanceCompanyType } from '@/models/sales-manager/advance-money'

type Props = {
  transaction?: TransactionType
  reservationId?: string
  checkinId?: string
  onAdjustSales: (selected: { salesPaymentId: string; accountsReceivableId: string; companyId: string }) => void
  onClose: (isUpdated: boolean) => void
  filterGuestRoomAssignId?: string[]
  advanceCompany: AdvanceCompanyType
  salesPayments: PaymentSettingType[]
  accountsReceivableSettings: AccountsReceivableSettingType[]
  isMastersLoaded: boolean
}

type CascaderOptionType = {
  value: string
  label: string
  disabled?: boolean
  code?: string
  children?: Array<CascaderOptionType>
}

export const AdjustmentPaymentForm: React.FC<Props> = props => {
  const { t, i18n } = useTranslation()
  const [isVisiblePaymentForm, setIsVisiblePaymentForm] = useState<boolean>(false)

  const [companyId, setCompanyId] = useState<string>('')
  const defaultPaymentOption = { value: '', label: t('Please select') }
  const [salesPaymentId, setSalesPaymentId] = useState<string>('')
  // 取得完了前は選択肢を出さず、支払方法の選択肢を入金会社なしで組み立てないようにする
  const salesPaymentOptions = useMemo<CascaderOptionType[]>(() => {
    if (!props.isMastersLoaded) return [defaultPaymentOption]
    const paymentCompanies: CascaderOptionType[] = props.advanceCompany.paymentCompanies.map(item => ({
      value: item.id,
      label: item.name,
      disabled: !item.currentBalance,
    }))
    const { paymentMethod } = props.advanceCompany
    const advancePaymentMethodId = Array.isArray(paymentMethod) ? undefined : paymentMethod.id
    return [
      defaultPaymentOption,
      ...props.salesPayments.map(item => ({
        value: item.id,
        label: item.name,
        children: item.id === advancePaymentMethodId ? paymentCompanies : undefined,
        code: item.methodCode,
      })),
    ]
  }, [props.isMastersLoaded, props.salesPayments, props.advanceCompany, t])

  const defaultReceivableOption = { value: '', label: '-' }
  const [accountsReceivableId, setAccountsReceivableId] = useState<string>('')
  const accountsReceivableOptions = useMemo<CascaderOptionType[]>(
    () => [
      defaultReceivableOption,
      ...props.accountsReceivableSettings.map(item => ({
        value: item.id,
        label: item.name,
        code: item.code,
      })),
    ],
    [props.accountsReceivableSettings],
  )

  const [showPosCalculateModal, setShowPosCalculateModal] = useState<boolean>(false)
  const [showTerminalCalculateModal, setShowTerminalCalculateModal] = useState<boolean>(false)

  const { plugins } = useContext<any>(AccountContext)
  const isEnabledSmaregi = checkActivePlugin(plugins, PluginCode.Smaregi)
  const isEnabledPaymentTerminal = checkActivePlugin(plugins, PluginCode.PaymentTerminal) && localStorage.getItem(terminalStorageKey)

  const onChangePaymentForm = () => {
    setIsVisiblePaymentForm(!isVisiblePaymentForm)
    setSalesPaymentId('')
    setCompanyId('')
    setAccountsReceivableId('')
  }

  const displayRender = label => label[label.length - 1]

  const _adjustSales = () => {
    props.onAdjustSales({ salesPaymentId, accountsReceivableId, companyId })
    setIsVisiblePaymentForm(false)
  }

  const changedSalesPayment = (value: string[]) => {
    setSalesPaymentId(value[0])
    autoCompleteWithPaymentMethod(value[0])
    setCompanyId(value.length === 2 ? value[1] : '')
  }

  const autoCompleteWithPaymentMethod = (paymentMethodMasterId: string) => {
    const selectedPaymentMethod = salesPaymentOptions.find(salesPayment => salesPayment.value === paymentMethodMasterId)
    const alignmentAccountReceivable = accountsReceivableOptions?.find(receivable => receivable.code === selectedPaymentMethod?.code)
    setAccountsReceivableId(alignmentAccountReceivable ? alignmentAccountReceivable.value : '')
  }

  if (isVisiblePaymentForm) {
    return (
      <div css={salesPaymentContainerStyle}>
        <div css={textStyle}>
          {t('Method of payment')}
          <sup>{i18n.language === 'en' ? '*' : '※'}</sup>
        </div>
        <div css={salesPaymentSelectWrapper}>
          <Cascader
            options={salesPaymentOptions}
            expandTrigger="hover"
            className={'edit-mode-cascader cascader-customize'}
            displayRender={displayRender}
            value={[salesPaymentId, companyId]}
            popupClassName="popup-cascader-customize"
            showSearch={cascaderNameSearch}
            onChange={changedSalesPayment}
            allowClear={false}
            suffixIcon={<span></span>}
          />
        </div>

        <div css={textStyle}>{t('Accounts receivable')}</div>
        <div css={salesPaymentSelectWrapper}>
          <Select
            showSearch
            optionFilterProp="children"
            filterOption={true}
            suffixIcon={<img src={require('@/static/images/arrow_down_yellow.svg')} />}
            onChange={value => setAccountsReceivableId(value)}
            value={accountsReceivableId}
          >
            {accountsReceivableOptions.map((accountsReceivable, index) => (
              <Select.Option key={index} value={accountsReceivable.value}>
                {accountsReceivable.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div css={buttonsWrapperStyle}>
          <Button buttonType={3} width={100} height={32} fontSize={12} marginRight={16} onClick={onChangePaymentForm}>
            {t('Cancel')}
          </Button>
          <Button
            width={80}
            height={32}
            buttonType={salesPaymentId ? 1 : 5}
            fontSize={12}
            onClick={_adjustSales}
            isDisabled={!salesPaymentId}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    )
  }
  return (
    <>
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <Button width={'100%'} height={32} buttonType={3} onClick={onChangePaymentForm}>
          {t('Calculate')}
        </Button>
        {isEnabledSmaregi && (
          <Button
            width={isEnabledPaymentTerminal ? 'calc(50% - 8px)' : '100%'}
            height={32}
            buttonType={3}
            onClick={() => setShowPosCalculateModal(true)}
          >
            {t('Send to Smaregi')}
          </Button>
        )}
        {isEnabledPaymentTerminal && (
          <Button
            width={isEnabledSmaregi ? 'calc(50% - 8px)' : '100%'}
            height={32}
            buttonType={ButtonType.Secondary}
            onClick={() => setShowTerminalCalculateModal(true)}
          >
            {t('Send to Terminal')}
          </Button>
        )}
      </div>
      {showPosCalculateModal && (
        <PosCalculateModal
          onClose={(isUpdated: boolean) => {
            setShowPosCalculateModal(false)
            props.onClose(isUpdated)
          }}
          reservationId={props.reservationId}
          transaction={props.transaction}
          checkinId={props.checkinId}
          filterGuestRoomAssignId={props.filterGuestRoomAssignId}
        />
      )}
      {showTerminalCalculateModal && (
        <TerminalCalculateModal
          onClose={(isUpdated: boolean) => {
            setShowTerminalCalculateModal(false)
            props.onClose(isUpdated)
          }}
          reservationId={props.reservationId}
          transaction={props.transaction}
          checkinId={props.checkinId}
          filterGuestRoomAssignId={props.filterGuestRoomAssignId}
        />
      )}
    </>
  )
}
const salesPaymentContainerStyle = css({
  border: '1px solid #CCCCCC',
  borderRadius: 5,
  background: '#F2F2F2',
  padding: 24,
  marginTop: 16,
})
const textStyle = css({
  fontWeight: 'bold',
  color: '#676767',
  fontSize: 12,
})
const buttonsWrapperStyle = css({
  display: 'flex',
  justifyContent: 'center',
})
const salesPaymentSelectWrapper = css({
  margin: '12px 0 16px 0',
  '.ant-cascader-picker-label': {
    fontSize: 14,
    fontWeight: 'normal',
    letterSpacing: '1.4px',
    color: '#676767',
  },
  '.ant-cascader-picker-show-search.ant-cascader-picker-focused:not(.ant-cascader-picker-with-value) .ant-cascader-picker-label': {
    color: 'rgba(103, 103, 103, 0.3)',
  },
  '.ant-cascader-picker-with-value .ant-cascader-picker-label': {
    color: 'transparent',
  },
  '.ant-cascader-picker-label:hover + .ant-cascader-input': {
    borderColor: '#CCCCCC',
    borderRightWidth: 'unset',
  },
  '.ant-cascader-picker:focus .ant-cascader-input': {
    borderColor: '#CCCCCC',
    borderRightWidth: 'unset',
    boxShadow: 'unset',
  },
  '.ant-input:focus': {
    boxShadow: 'unset',
  },
  '.cascader-customize': {
    width: '100%',
    marginRight: '0.5%',
    marginLeft: 0,
    height: 32,
    borderRadius: 16,
    input: {
      borderRadius: 16,
      border: '1px solid #CCCCCC',
    },
  },
  '.edit-mode-cascader': {
    backgroundImage: `url(${require('@/static/images/select.svg')})`,
    backgroundPosition: '95% center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 9,
  },
})
